import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Plus, Pencil, Eye, Trash2 } from 'lucide-react';

// ============================================================================
// LibraryVersionsMenu
// ============================================================================
// Archive picker for the QS Pro library, shaped like the timeline versions
// dropdown (see VersionsDropdown in src/pages/projects/TimelinePage.jsx):
// "Add version" first, then "Current (editable)", then each saved archive.
// Selecting an archive puts the library page into read-only view mode.
// ============================================================================

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '');

export const archiveLabel = (a) => `V${a.seq}: ${fmtDate(a.snapshotAt)}${a.label ? ` — ${a.label}` : ''}`;

function LibraryVersionsMenu({ archives, viewingId, onAdd, onSelect, onReturnToCurrent, onDelete, busy, canDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active = archives.find((a) => a.id === viewingId);
  const itemBtn = {
    display: 'flex', alignItems: 'center', gap: '6px', width: '100%', textAlign: 'left',
    background: 'none', border: 'none', cursor: 'pointer', padding: '5px 10px',
    fontSize: '0.78rem', color: 'var(--brio-text, #333)',
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Library archives — save a copy of the whole library, or view an earlier one"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '5px',
          padding: '0.35rem 0.7rem', fontSize: '0.82rem',
          border: '1px solid ' + (viewingId ? 'var(--brio-gold, #9F902A)' : 'var(--brio-border, #ccc)'),
          background: viewingId ? 'var(--brio-gold-light, rgba(159,144,42,0.12))' : '#fff',
          color: viewingId ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text, #333)',
          cursor: 'pointer', borderRadius: 4,
        }}
      >
        <span>{active ? archiveLabel(active) : 'Versions'}</span>
        <ChevronDown size={13} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)',
          minWidth: 260, maxHeight: 340, overflowY: 'auto',
          background: '#fff', border: '1px solid var(--brio-border, #ccc)', borderRadius: 3,
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)', zIndex: 60, padding: '4px 0',
        }}>
          <button
            type="button"
            style={{ ...itemBtn, color: 'var(--brio-gold, #9F902A)', fontWeight: 600, opacity: busy ? 0.6 : 1 }}
            disabled={busy}
            onClick={() => { setOpen(false); onAdd(); }}
          >
            <Plus size={13} /> {busy ? 'Working…' : 'Add version'}
          </button>
          <div style={{ height: 1, background: 'var(--brio-border, #ccc)', margin: '4px 0' }} />
          <button
            type="button"
            style={{ ...itemBtn, fontWeight: viewingId ? 400 : 600, background: viewingId ? 'none' : 'var(--brio-surface-muted, #f7f7f5)' }}
            onClick={() => { setOpen(false); onReturnToCurrent(); }}
          >
            <Pencil size={13} /> Current (editable)
          </button>
          {archives.length === 0 && (
            <div style={{ padding: '6px 10px', fontSize: '0.74rem', color: 'var(--brio-text-muted)' }}>No versions yet</div>
          )}
          {archives.map((a) => {
            const isActive = a.id === viewingId;
            return (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', background: isActive ? 'var(--brio-gold-light, rgba(159,144,42,0.12))' : 'none' }}>
                <button
                  type="button"
                  title={`${a.code} · ${a.itemCount} items · ${a.versionCount} versions · ${a.rateCount} rates`}
                  style={{ ...itemBtn, flex: 1, minWidth: 0, paddingRight: canDelete ? 0 : 10, fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--brio-gold, #9F902A)' : 'var(--brio-text, #333)' }}
                  onClick={() => { setOpen(false); onSelect(a); }}
                >
                  <Eye size={13} style={{ flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{archiveLabel(a)}</span>
                  {a.isAuto && <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)', flexShrink: 0 }}>auto</span>}
                </button>
                {/* Delete is admin-plus-allow-list only (boq.can_delete_library_version).
                    Hidden rather than disabled: for everyone else it is not an
                    action that is temporarily unavailable, it is not theirs. */}
                {canDelete && (
                  <button
                    type="button"
                    title="Delete this archive"
                    onClick={(e) => { e.stopPropagation(); onDelete(a); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', color: 'var(--brio-text-muted)', flexShrink: 0 }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LibraryVersionsMenu;
